import {
  Box, Container, SimpleGrid, Stack, Group, Text, Anchor, Image, Divider, ActionIcon,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconBrandInstagram, IconBrandFacebook, IconBrandTelegram, IconBrandWhatsapp, IconMail,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicAsset } from '@/utils/images';
import classes from './Footer.module.css';

// Contact channels are placeholders until real handles are provided.
const SUPPORT_EMAIL = 'support@teletienda.ar';
const SOCIAL_LINKS = [
  { label: 'Instagram', href: '#', icon: IconBrandInstagram },
  { label: 'Facebook', href: '#', icon: IconBrandFacebook },
  { label: 'Telegram', href: '#', icon: IconBrandTelegram },
  { label: 'WhatsApp', href: '#', icon: IconBrandWhatsapp },
];

/**
 * Site-wide footer: brand + sitemap + legal links + contact channels and a
 * copyright bar. Mounted once in AppShell so it renders on every page.
 */
export function Footer() {
  const { t } = useTranslation('common');
  const computedColorScheme = useComputedColorScheme('light');
  const year = new Date().getFullYear();

  const exploreLinks = [
    { label: t('home'), to: '/' },
    { label: t('search'), to: '/search' },
    { label: t('merchants'), to: '/merchants' },
    { label: t('nav_news'), to: '/news' },
  ];

  const legalLinks = [
    { label: t('legal.terms.title'), to: '/terms' },
    { label: t('legal.privacy.title'), to: '/privacy' },
    { label: t('delete_account'), to: '/deleteme' },
  ];

  return (
    <Box component="footer" className={classes.footer} mt="xl">
      <Container size="lg" py="xl">
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl">
          {/* Brand */}
          <Stack gap="sm">
            <Image
              src={publicAsset(computedColorScheme === 'dark' ? 'images/logo-white.png' : 'images/logo-black.png')}
              alt="TeleTienda"
              h={32}
              w="auto"
              fit="contain"
              style={{ alignSelf: 'flex-start' }}
            />
            <Text size="sm" c="dimmed" maw={260}>
              {t('footer_tagline')}
            </Text>
          </Stack>

          {/* Explore / sitemap */}
          <Stack gap="xs" component="nav" aria-label={t('footer_explore')}>
            <Text fw={700} size="sm" tt="uppercase" c="dimmed">
              {t('footer_explore')}
            </Text>
            {exploreLinks.map((l) => (
              <Anchor key={l.to} component={Link} to={l.to} size="sm" c="dimmed" className={classes.link}>
                {l.label}
              </Anchor>
            ))}
          </Stack>

          {/* Legal */}
          <Stack gap="xs" component="nav" aria-label={t('footer_legal')}>
            <Text fw={700} size="sm" tt="uppercase" c="dimmed">
              {t('footer_legal')}
            </Text>
            {legalLinks.map((l) => (
              <Anchor key={l.to} component={Link} to={l.to} size="sm" c="dimmed" className={classes.link}>
                {l.label}
              </Anchor>
            ))}
          </Stack>

          {/* Contact */}
          <Stack gap="sm">
            <Text fw={700} size="sm" tt="uppercase" c="dimmed">
              {t('footer_contact')}
            </Text>
            <Anchor href={`mailto:${SUPPORT_EMAIL}`} size="sm" c="dimmed" className={classes.link}>
              <Group gap={6} wrap="nowrap">
                <IconMail size={16} />
                {SUPPORT_EMAIL}
              </Group>
            </Anchor>
            <Group gap="xs">
              {SOCIAL_LINKS.map(({ label, href, icon: Icon }) => (
                <ActionIcon
                  key={label}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="subtle"
                  color="gray"
                  aria-label={label}
                >
                  <Icon size={20} stroke={1.5} />
                </ActionIcon>
              ))}
            </Group>
          </Stack>
        </SimpleGrid>

        <Divider my="lg" />

        <Text size="xs" c="dimmed" ta="center">
          © {year} TeleTienda. {t('footer_rights')}
        </Text>
      </Container>
    </Box>
  );
}

export default Footer;
